// ISDP CUSTOM: shared DevSecOps UI pieces — per-tool icons/logos, a dropdown multi-select (replaces the
// ugly native <select multiple> and checkbox-list pickers), and a small loading spinner.
import { useEffect, useRef, useState } from 'react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { devsecopsMessages } from '../../i18n/devsecops';
// Real per-tool logo assets, carried over from the old Hub frontend (Components/ProjectContent/Icons).
import githubLogo from './icons/github.png';
import idmsProdLogo from './icons/idmsProd.png';
import idmsDevLogo from './icons/idmsDev.png';
import doopLogo from './icons/DOOP.png';
import doopDevLogo from './icons/DOOPDev.png';
import keyChangerLogo from './icons/keyChangerProd.png';
import azureDevOpsLogo from './icons/azure-devops-logo.png';
import azureLogo from './icons/azure.png';
import appleLogo from './icons/AppleAppstore.png';
import blackduckLogo from './icons/blackduck.png';
import bookmarkLogo from './icons/bookmark.png';
import firebaseLogo from './icons/firebase.png';
import gcpLogo from './icons/googleCloudServices.png';
import googlePlayLogo from './icons/googlePlayStoreConsole.png';
import sendgridLogo from './icons/sendgrid.png';
import sentryLogo from './icons/sentry.png';
import sonarqubeLogo from './icons/sonarqube.png';
import testFairyLogo from './icons/testFairy.png';

// staticModuleId -> real logo asset (mirrors the old Hub's moduleIcons map). Tools without a logo
// (DPAS, DAMS) fall back to the ODX glyph from moduleIcon().
const ASSET_BY_ID: Record<string, string> = {
  gitHubEnterpriseDrg: githubLogo,
  idmsProd: idmsProdLogo, idmsDev: idmsDevLogo,
  doopProd: doopLogo, doopDev: doopDevLogo,
  keyChangerProd: keyChangerLogo,
  msAzureDevOpsDrg: azureDevOpsLogo, azurePortal: azureLogo,
  appleStoreConnect: appleLogo,
  blackduck: blackduckLogo,
  customBookmark: bookmarkLogo,
  firebase: firebaseLogo,
  googlecloudservices: gcpLogo, googlePlay: googlePlayLogo,
  sendgrid: sendgridLogo, sentryio: sentryLogo, sonarqube: sonarqubeLogo,
  testFairy: testFairyLogo,
};

export const moduleAsset = (staticModuleId: string): string | undefined => ASSET_BY_ID[staticModuleId];

// Renders a module's real logo when one exists, else the per-tool ODX glyph. Drop inside a .dso-avatar.
export function ModuleAvatar({ staticModuleId }: { staticModuleId: string }) {
  const asset = moduleAsset(staticModuleId);
  return asset
    ? <img className="dso-modimg" src={asset} alt="" />
    : <odx-icon name={moduleIcon(staticModuleId)} size="md" />;
}

// The backend's static-module `icon` field is just a category label ("Lifecycle"/"IDMS"/…), not a real
// icon, so we map each tool to a registered ODX core icon. Only icons imported in index.tsx render
// (others are blank), so every value here must be registered there.
const ICON_BY_ID: Record<string, string> = {
  idmsProd: 'core::group', idmsDev: 'core::group',
  doopProd: 'core::shoppingcart', doopDev: 'core::shoppingcart',
  dpas: 'core::picture',
  gitHubEnterpriseDrg: 'core::folder-team',
  sonarqube: 'core::information-security',
  blackduck: 'core::information-security',
  customBookmark: 'core::bookmark',
  sendgrid: 'core::mail',
  sentryio: 'core::warning',
  keyChangerProd: 'core::lock',
  msAzureDevOpsDrg: 'core::cloud', azurePortal: 'core::cloud',
  firebase: 'core::cloud', googlecloudservices: 'core::cloud',
  appleStoreConnect: 'core::phone', googlePlay: 'core::phone', testFairy: 'core::phone',
};

export function moduleIcon(staticModuleId: string): string {
  if (ICON_BY_ID[staticModuleId]) return ICON_BY_ID[staticModuleId];
  const s = staticModuleId || '';
  if (s.startsWith('idms')) return 'core::group';
  if (s.startsWith('doop')) return 'core::shoppingcart';
  if (s.startsWith('gitHub')) return 'core::folder-team';
  if (s.startsWith('dpas')) return 'core::picture';
  return 'core::object';
}

// a11y helper: fire the same handler on Enter/Space for elements that use onClick on a non-native
// interactive element (paired with role="button" + tabIndex). Keeps click semantics identical.
export const onEnterOrSpace =
  (handler: () => void) =>
  (e: { key: string; preventDefault: () => void }) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };

// Email validation used across the DevSecOps hub (IDMS mail templates, DOOP
// contractors, project contacts, permission add-user). The check itself now lives in
// the UI kit (components/common/validation) so callers outside this hub — e.g. the
// entity About card's Contact row — can use it without importing this module's ~20
// logo PNGs. Re-exported here so the hub's own imports stay as they are.
export { isValidEmail } from '../common/validation';

export interface MsOption { value: string; label: string }

// Dropdown multi-select: a button showing a summary, opening a checkbox popover. Closes on outside click.
export function DsMultiSelect({
  options, value, onChange, placeholder, allLabel,
}: {
  options: MsOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  allLabel?: string;
}) {
  const { t } = useTranslationRef(devsecopsMessages);
  const ph = placeholder ?? t('common.any');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (v: string) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const computeSummary = (): string => {
    if (value.length === 0) return allLabel ?? ph;
    if (value.length === 1) return options.find(o => o.value === value[0])?.label ?? value[0];
    return t('multiSelect.selected', { num: String(value.length) });
  };
  const summary = computeSummary();

  return (
    <div className="dso-ms" ref={ref}>
      <button type="button" className="dso-ms__btn" onClick={() => setOpen(o => !o)}>
        <span className={value.length ? '' : 'dso__muted'}>{summary}</span>
        <odx-icon name="core::chevron-down" size="sm" />
      </button>
      {open && (
        <div className="dso-ms__pop">
          {options.length === 0 ? (
            <div className="dso-ms__empty">{t('multiSelect.noOptions')}</div>
          ) : options.map(o => (
            <label key={o.value} className="dso-ms__opt">
              <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Small inline loading indicator (animated ring via CSS ::before + a label).
export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslationRef(devsecopsMessages);
  return <div className="dso-spinner">{label ?? t('common.loading')}</div>;
}

/**
 * Build and trigger a CSV download from a header row + data rows.
 * Values are quoted only when they contain commas, double-quotes, or newlines.
 * Used by ModuleTabContent and (via dsoShared) other DSO pages.
 */
export function downloadCsv(
  filename: string,
  headers: string[],
  rows: (string | number | null | undefined)[][],
): void {
  const esc = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
